import { useState, useEffect } from 'react';
import { useNavigate, NavLink, Link, Outlet } from 'react-router-dom';
import EgoLogo from './molecules/EgoLogo';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserStatus } from '../context/UserStatusContext';
import GatedFeature from './atoms/GatedFeature';
import InfoModal from './atoms/InfoModal';
import styles from './Layout.module.css';
import { acceptTerms } from '../services/apiService';

const TERMS_VERSION = '1.0';

const Layout = () => {
  const navigate = useNavigate();
  const {
    userStatus,
    refreshUserStatus,
    isLoading: isStatusLoading,
  } = useUserStatus();
  const {
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
    user,
    isLoading: isAuthLoading,
  } = useAuth0();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const creditInfo = {
    icon: 'fas fa-bolt',
    title: 'Ego Credits',
    content:
      'Ego Credits are your personal budget for interacting with and creating personas in your private workspace. Credits reset monthly.',
    actionText: 'View Plans',
    onActionClick: () => navigate('/pricing'),
  };

  const engagementInfo = {
    icon: 'fas fa-fire',
    title: 'Monthly Engagement',
    content:
      "This is your public popularity quota. It's used when unauthenticated visitors chat with your public personas. Running out is a good thing - it means you're popular!",
    actionText: 'View Plans',
    onActionClick: () => navigate('/pricing'),
  };

  useEffect(() => {
    if (isStatusLoading) {
      return;
    }
    if (userStatus && userStatus.termsAcceptedVersion !== TERMS_VERSION) {
      setShowTermsModal(true);
    } else {
      setShowTermsModal(false);
    }
  }, [userStatus, isStatusLoading]);

  const handleAcceptTerms = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      await acceptTerms({ version: TERMS_VERSION }, accessToken);
      await refreshUserStatus();
      setShowTermsModal(false);
    } catch (error) {
      console.error('Failed to accept terms:', error);
    }
  };

  if (isAuthLoading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className={styles.appContainer}>
      <aside className={`${styles.sideNav} ${isNavOpen ? styles.navOpen : ''}`}>
        <div className={styles.navTop}>
          <Link
            to="/"
            className={styles.logo}
            onClick={() => setIsNavOpen(false)}
          >
            Ego Console
          </Link>
          <nav className={styles.mainNav}>
            <NavLink
              to="/passport"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={() => setIsNavOpen(false)}
            >
              <EgoLogo className={styles.navIcon} />
              <span>Digital Passport</span>
            </NavLink>
            <NavLink
              to="/profiles"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={() => setIsNavOpen(false)}
            >
              <i className="fas fa-user-friends"></i>
              <span>Public Personas</span>
            </NavLink>
            <NavLink
              to="/community"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={() => setIsNavOpen(false)}
            >
              <i className="fas fa-globe"></i>
              <span>Community</span>
            </NavLink>
            <GatedFeature requiredTier="premium" displayMode="inline">
              <NavLink
                to="/analytics"
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={() => setIsNavOpen(false)}
              >
                <i className="fas fa-chart-line"></i>
                <span>Analytics</span>
              </NavLink>
            </GatedFeature>
            <GatedFeature requiredTier="premium" displayMode="inline">
              <NavLink
                to="/developer"
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={() => setIsNavOpen(false)}
              >
                <i className="fas fa-code"></i>
                <span>Developer</span>
              </NavLink>
            </GatedFeature>
          </nav>
        </div>
        <div className={styles.navBottom}>
          <NavLink
            to="/pricing"
            className={({ isActive }) => (isActive ? styles.active : '')}
            onClick={() => setIsNavOpen(false)}
          >
            <i className="fas fa-credit-card"></i>
            <span>Pricing & Billing</span>
          </NavLink>
          <NavLink
            to="/shop"
            className={({ isActive }) => (isActive ? styles.active : '')}
            onClick={() => setIsNavOpen(false)}
          >
            <i className="fas fa-shopping-bag"></i>
            <span>Shop</span>
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) => (isActive ? styles.active : '')}
            onClick={() => setIsNavOpen(false)}
          >
            <i className="fas fa-cog"></i>
            <span>Account</span>
          </NavLink>
          <button
            className={styles.logoutButton}
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      <div className={styles.mainWrapper}>
        <header className={styles.appHeader}>
          <button
            className={styles.hamburger}
            onClick={() => setIsNavOpen(!isNavOpen)}
            aria-label="Toggle navigation"
          >
            <i className={isNavOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
          </button>
          <div className={styles.headerRight}>
            <div className={styles.authControls}>
              <button
                type="button"
                className={styles.creditButton}
                title="Monthly Ego Credits Remaining"
                onClick={() => setInfoModalContent(creditInfo)}
              >
                ⚡️ {userStatus?.creatorCredits ?? 0}
              </button>
              <button
                type="button"
                className={styles.creditButton}
                title="Monthly Public Engagement"
                onClick={() => setInfoModalContent(engagementInfo)}
              >
                🔥 {userStatus?.monthlyEngagementUsed ?? 0} /{' '}
                {userStatus?.monthlyEngagementQuota ?? 0}
              </button>
              <span className={styles.userEmail}>{user?.email}</span>
            </div>
          </div>
        </header>

        <main className={styles.contentArea}>
          <Outlet />
        </main>
        <footer className={styles.appFooter}>
          <span>© 2025 SoulstoreAI LLC. All Rights Reserved.</span>
          <div className={styles.footerLinks}>
            <Link to="/faq">FAQ</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </footer>
      </div>

      {showTermsModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Welcome to the Ego Beta v2!</h3>
            <p>
              By continuing, you agree to our updated{' '}
              <Link to="/terms" target="_blank">
                Terms of Service
              </Link>{' '}
              and acknowledge our{' '}
              <Link to="/privacy" target="_blank">
                Privacy Policy
              </Link>
              . Enjoy!
            </p>
            <div className={styles.modalActions}>
              <button
                onClick={handleAcceptTerms}
                className="action-button primary"
              >
                Agree & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <InfoModal
        isOpen={!!infoModalContent}
        onClose={() => setInfoModalContent(null)}
        {...infoModalContent}
      />
    </div>
  );
};

export default Layout;
